import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Badge,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccessTime,
  Warning,
  CheckCircle,
  Cancel,
  Edit,
  Delete,
  GetApp,
  Refresh,
  ExitToApp,
  TrendingUp,
  Group,
  Schedule,
  DateRange
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import AttendanceAnalytics from './AttendanceAnalytics';

const AttendanceDashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [report, setReport] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
    departmentId: '',
    userId: '',
    status: ''
  });
  const [editDialog, setEditDialog] = useState({ open: false, record: null });
  const [bulkClockOutDialog, setBulkClockOutDialog] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchDepartments();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchReport();
    }
  }, [activeTab, filters]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/attendance/admin/dashboard');
      setDashboard(response.data);
    } catch (error) {
      enqueueSnackbar('Error fetching dashboard', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const params = {
        start_date: format(filters.startDate, 'yyyy-MM-dd'),
        end_date: format(filters.endDate, 'yyyy-MM-dd'),
        ...(filters.departmentId && { department_id: filters.departmentId }),
        ...(filters.userId && { user_id: filters.userId }),
        ...(filters.status && { status: filters.status })
      };
      const response = await api.get('/attendance/admin/report', { params });
      setReport(response.data);
    } catch (error) {
      enqueueSnackbar('Error fetching report', { variant: 'error' });
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data.filter(u => u.status === 'active'));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleForceClockOut = async (userId, reason) => {
    try {
      await api.post(`/attendance/force-clock-out/${userId}`, { 
        notes: reason || 'Admin forced clock out' 
      });
      enqueueSnackbar('User clocked out successfully', { variant: 'success' });
      fetchDashboard();
    } catch (error) {
      enqueueSnackbar('Error clocking out user', { variant: 'error' });
    }
  };

  const handleBulkClockOut = async (reason) => {
    try {
      const userIds = selectedUsers.length > 0 
        ? selectedUsers 
        : dashboard.currentlyWorking.map(u => u.user_id);
        
      await api.post('/attendance/admin/bulk-clock-out', {
        userIds,
        reason: reason || 'End of day bulk clock out'
      });
      
      enqueueSnackbar('Bulk clock out completed', { variant: 'success' });
      setBulkClockOutDialog(false);
      setSelectedUsers([]);
      fetchDashboard();
    } catch (error) {
      enqueueSnackbar('Error in bulk clock out', { variant: 'error' });
    }
  };

  const handleEditAttendance = async (updates) => {
    try {
      await api.put(`/attendance/admin/edit/${editDialog.record.id}`, updates);
      enqueueSnackbar('Attendance updated successfully', { variant: 'success' });
      setEditDialog({ open: false, record: null });
      fetchReport();
    } catch (error) {
      enqueueSnackbar('Error updating attendance', { variant: 'error' });
    }
  };

  const handleDeleteAttendance = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await api.delete(`/attendance/admin/delete/${id}`);
      enqueueSnackbar('Attendance deleted successfully', { variant: 'success' });
      fetchReport();
    } catch (error) {
      enqueueSnackbar('Error deleting attendance', { variant: 'error' });
    }
  };

  const exportReport = async (format = 'csv') => {
    try {
      const params = {
        ...filters,
        export_format: format
      };
      const response = await api.get('/attendance/admin/report', {
        params,
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_report_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar('Error exporting report', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Live Dashboard" icon={<DashboardIcon />} />
          <Tab label="Attendance Report" icon={<DateRange />} />
          <Tab label="Analytics" icon={<TrendingUp />} />
        </Tabs>
      </Box>

      {/* Live Dashboard Tab */}
      {activeTab === 0 && dashboard && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Currently Working
                    </Typography>
                    <Typography variant="h4">
                      {dashboard.currentlyWorking?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Late Arrivals
                    </Typography>
                    <Typography variant="h4">
                      {dashboard.lateArrivals?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      On Break
                    </Typography>
                    <Typography variant="h4">
                      {dashboard.onBreak?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Absent Today
                    </Typography>
                    <Typography variant="h4">
                      {dashboard.absentStaff?.length || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Currently Working List */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    Currently Working
                  </Typography>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => setBulkClockOutDialog(true)}
                    startIcon={<ExitToApp />}
                  >
                    Bulk Clock Out
                  </Button>
                </Box>
                <List>
                  {dashboard.currentlyWorking?.map((user) => (
                    <ListItem key={user.id}>
                      <ListItemAvatar>
                        <Avatar>{user.full_name[0]}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.full_name}
                        secondary={`${user.department_name} • Since ${format(parseISO(user.clock_in), 'HH:mm')}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          color="error"
                          onClick={() => handleForceClockOut(user.user_id)}
                        >
                          <ExitToApp />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Late Arrivals */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Late Arrivals Today
                </Typography>
                <List>
                  {dashboard.lateArrivals?.map((user) => (
                    <ListItem key={user.id}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <Warning />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.full_name}
                        secondary={`${user.minutes_late} minutes late`}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Attendance Report Tab */}
      {activeTab === 1 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  value={format(filters.startDate, 'yyyy-MM-dd')}
                  onChange={(e) => setFilters({ ...filters, startDate: new Date(e.target.value) })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  type="date"
                  label="End Date"
                  value={format(filters.endDate, 'yyyy-MM-dd')}
                  onChange={(e) => setFilters({ ...filters, endDate: new Date(e.target.value) })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={filters.departmentId}
                    onChange={(e) => setFilters({ ...filters, departmentId: e.target.value })}
                    label="Department"
                  >
                    <MenuItem value="">All</MenuItem>
                    {departments.map(dept => (
                      <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <Button
                  variant="contained"
                  startIcon={<GetApp />}
                  onClick={() => exportReport('csv')}
                  fullWidth
                >
                  Export CSV
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Report Table */}
          {report && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell>Clock In</TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell>Hours</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.records?.map((record) => (
                    <TableRow key={record.id}>
                      <TableCell>{format(parseISO(record.date), 'MMM dd')}</TableCell>
                      <TableCell>{record.full_name}</TableCell>
                      <TableCell>{record.department_name}</TableCell>
                      <TableCell>
                        {record.clock_in ? format(parseISO(record.clock_in), 'HH:mm') : '-'}
                      </TableCell>
                      <TableCell>
                        {record.clock_out ? format(parseISO(record.clock_out), 'HH:mm') : '-'}
                      </TableCell>
                      <TableCell>{record.total_hours ? parseFloat(record.total_hours).toFixed(1) : '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={record.status} 
                          size="small"
                          color={record.status === 'present' ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton 
                          size="small" 
                          onClick={() => setEditDialog({ open: true, record })}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteAttendance(record.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

  
{/* Analytics Tab */}
{activeTab === 2 && (
  <AttendanceAnalytics />
)}

      {/* Bulk Clock Out Dialog */}
      <Dialog
        open={bulkClockOutDialog}
        onClose={() => setBulkClockOutDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Clock Out</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will clock out all currently working users.
          </Alert>
          <TextField
            fullWidth
            label="Reason (Optional)"
            multiline
            rows={3}
            id="bulk-reason"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkClockOutDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={() => {
              const reason = document.getElementById('bulk-reason').value;
              handleBulkClockOut(reason);
            }}
          >
            Clock Out All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceDashboard;
